import area from '@turf/area';
import { inverseCoordinates } from '../../geo';
import { SOIL_SSURGO_ZONE_COLORS } from '../../../constants/colors';
import { SOILSURGO_LAYERS } from '../../../constants/mapLayerIds';

/**
 * Extract Soil SSURGO image ID
 * @param {Object} event - Mosaic V3 FHA Vegation events organized by time in ascending order
 * @return {Object} - Extracted Vegetation objects organized by year
 */
export function extractSoilSsurgo(event) {
  if (event && event.images && event.images.length) {
    const imageUri = event.images[0]['cache-uri'];
    const searchStr = '/download/';
    const imageIdIndex = imageUri.indexOf(searchStr) + searchStr.length;

    return {
      soilssurgo: {
        activeSubLayerIds: [0, 0],
        imageId: imageUri.slice(imageIdIndex),
      },
    };
  }

  return {};
}

/**
 * Combine any zones that share the same mukey
 * @param {Array} zones
 * @return {Array}
 */
export function combineSoilSsurgoZones(zones) {
  return zones.reduce((_zones, zone) => {
    const linkedZoneIndex = _zones.findIndex(
      (_zone) => _zone.properties.mukey === zone.properties.mukey
    );

    if (linkedZoneIndex > -1) {
      let coordinates;

      switch (
        `${_zones[linkedZoneIndex].geometry.type} ${zone.geometry.type}`
      ) {
        case 'Polygon Polygon': {
          coordinates = [
            _zones[linkedZoneIndex].geometry.coordinates,
            zone.geometry.coordinates,
          ];
          break;
        }
        case 'MultiPolygon Polygon': {
          coordinates = [
            ..._zones[linkedZoneIndex].geometry.coordinates,
            zone.geometry.coordinates,
          ];
          break;
        }
        case 'Polygon MultiPolygon': {
          coordinates = [
            _zones[linkedZoneIndex].geometry.coordinates,
            ...zone.geometry.coordinates,
          ];
          break;
        }
        case 'MultiPolygon MultiPolygon': {
          coordinates = [
            ..._zones[linkedZoneIndex].geometry.coordinates,
            ...zone.geometry.coordinates,
          ];
          break;
        }
        default: {
          return _zones.concat(zone);
        }
      }

      const combinedZone = {
        ..._zones[linkedZoneIndex],
        geometry: {
          ..._zones[linkedZoneIndex].geometry,
          coordinates,
          type: 'MultiPolygon',
        },
      };

      return [
        ..._zones.slice(0, linkedZoneIndex),
        combinedZone,
        ..._zones.slice(linkedZoneIndex + 1),
      ];
    }

    return _zones.concat(zone);
  }, []);
}

/**
 * returns an empty object for each element of the array
 * @returns {number}
 */
export const getSoilsSurgolayersValues = Object.values(SOILSURGO_LAYERS).reduce(
  (acum, value) => ({
      ...acum,
      [value]: {},
    }),
  {}
);

/**
 * Compare 2 soil SSURGO zones
 * @param {Object} zone1
 * @param {Object} zone2
 * @return {Array}
 */
export function compareSoilSsurgoZones(zone1, zone2) {
  if (zone1.properties.muname < zone2.properties.muname) {
    return -1;
  }
  if (zone1.properties.muname > zone2.properties.muname) {
    return 1;
  }
  return 0;
}

/**
 * Extract required soil SSURGO zone metadata
 * @param {Array} zones
 * @return {Array}
 */
export function extractSoilSsurgoZoneData(zones) {
  return zones.map((zone, index) => ({
    coordinates: inverseCoordinates(zone.geometry.coordinates),
    area: area(zone),
    color: SOIL_SSURGO_ZONE_COLORS[index % SOIL_SSURGO_ZONE_COLORS.length],
    drainage: zone.properties['drainage-class'],
    soilType: zone.properties.muname,
    soilTypeAbbreviated: zone.properties['parsed-muname'],
  }));
}

/**
 * Extract zone coordinates and metadata info
 * @param {Array} zones
 * @return {Array}
 */
export function extractSoilSsurgoZones(zones) {
  const combinedZones = combineSoilSsurgoZones(zones);
  combinedZones.sort(compareSoilSsurgoZones);
  return extractSoilSsurgoZoneData(combinedZones);
}
